import { Injectable } from '@angular/core';
import Utils from '../../../app/shared/utils/utils';
import { GameControlFlowTypeConfigOptions } from '../../../config/game-control-flow-type-config.interface';
import { GameControlFlowCategory } from '../../game/enum/game-control-flow-category.enum';
import { FormControlOptions } from '../interfaces/form-control-options.interface';
import { FormControlSelectOptions } from '../interfaces/form-control-select-options.interface';
import { Dictionary } from '../models/dictionary';
import { GameUtilHelper } from '../models/helper/game-util-helper';
import { ConfigurationService } from '../services/configuration.service';

@Injectable({
  providedIn: 'root'
})
export class GameControlFlowTypeUtils {

  private controlFlowTypeValues;

  constructor(
    private readonly config: ConfigurationService,
    private readonly gameUtilHelper: GameUtilHelper,
  ) {
  }

  private getControlFlowTypes(): GameControlFlowTypeConfigOptions {
    if (this.config.isLoadingConfig()) {
      return undefined;
    }

    const config = this.config.getControlFlowTypeConfig();
    if (config) {
      return Utils.clone(config);
    }

    return undefined;
  }

  getTypeConfig(type: number) {
    return this.getControlFlowTypes()[ type ];
  }

  getLabel(controlFlowType: string | number) {
    if (this.getControlFlowTypes() && this.isValid(controlFlowType)) {
      return this.getControlFlowTypes()[ Number(controlFlowType) ].label;
    }

    return '';
  }

  hasField(controlFlowType: string | number, field: string): boolean {
    if (this.getControlFlowTypes()) {
      return Object.keys(this.getControlFlowTypes()[ Number(controlFlowType) ].fields).indexOf(field) >= 0;
    }

    return false;
  }

  getFieldOptions(controlFlowType: string | number, field: string, defaultOptions: FormControlOptions<any>): FormControlOptions<any> {
    if (this.getControlFlowTypes()) {
      const options = this.hasField(controlFlowType, field) ? Utils.clone(this.getControlFlowTypes()[ Number(controlFlowType) ].fields[ field ]) : {};

      for (const optionName in defaultOptions) {
        if (Utils.isDefined(defaultOptions[ optionName ])) {
          options[ optionName ] = Utils.isDefined(options[ optionName ]) ? options[ optionName ] : defaultOptions[ optionName ];
        }
      }

      options.key = defaultOptions.key || field;
      options.hidden = Utils.isDefined(defaultOptions.hidden) ? defaultOptions.hidden : !this.hasField(controlFlowType, field);

      return options;
    }

    return {};
  }

  getTypeValues(): number[] {
    if (this.getControlFlowTypes()) {
      this.controlFlowTypeValues = this.controlFlowTypeValues || Object.keys(this.getControlFlowTypes())
        .filter(Number)
        .map(controlFlowType => Number(controlFlowType));

      return Utils.clone(this.controlFlowTypeValues);
    }

    return [];
  }

  getCategoryOfType(type: number): string {
    const config = this.getControlFlowTypes()[ type ];

    return config && config.category;
  }

  getCategoryValues(): string[] {
    return Object.values(GameControlFlowCategory);
  }

  getDefaultType(): number {
    return this.getTypeValues()[ 0 ] || 0;
  }

  getDefaultCategory(): string {
    return this.getCategoryValues()[ 0 ];
  }

  isValid(controlFlowType: string | number): boolean {
    return !isNaN(Number(controlFlowType)) && this.getTypeValues().indexOf(Number(controlFlowType)) >= 0;
  }

  getTypeSelectOptions(): FormControlSelectOptions<number> {
    return this.getTypeValues()
      .map(controlFlowType => {
        return {
          value: controlFlowType,
          label: this.getLabel(controlFlowType)
        };
      });
  }

  getCategorySelectOptions(): FormControlSelectOptions<string> {
    return this.getCategoryValues()
      .map(value => ({ value, label: value.toUpperCase() }));
  }

  getModules(controlFlowType: string | number): string[] {
    if (this.getControlFlowTypes()) {
      return Utils.clone(this.getControlFlowTypes()[ Number(controlFlowType) ].modules);
    }

    return [];
  }

  isModuleEnabled(controlFlowType: string | number, module: string): boolean {
    return this.getModules(controlFlowType).indexOf(module) >= 0;
  }

  createDictionary(levelType: string | number, key: 'parameters' | 'textDictionary' | 'assetDictionary'): any[] {

    const types = this.getControlFlowTypes();

    if (levelType && types) {
      const dict: Dictionary<any> = types[ Number(levelType) ];

      return this.gameUtilHelper.createDictionary(dict, key);
    }

    return [];
  }
}
